import { FC, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import FooterNav from './Navigation';
import { ArrowRight } from 'components/library/svgs--foldertentative/arrow-right';
import { footerNavContent } from './Navigation/navigation.content';
import useClientSideMediaQuery from 'hooks/use-client-side-media-query';
import { identifyAndTrackEvent } from 'lib/analytics';
import NextImage from 'next/image';
import {
  MicroScope,
  PlanetCapsule,
  UnionIcon,
} from 'components/library/svgs--foldertentative/footer-icons';
import {
  FooterWrapper,
  FooterInner,
  FooterContent,
  FooterForm,
  FooterHeading,
  FooterSubhead,
  Copyright,
  FooterFixedBottom,
  FixedHeadlineWrapper,
  FooterNewsletter,
  FooterCaption,
  FormAlert,
  Input,
  Submit,
  FooterNavColumn,
  FooterCountrySelectorArea,
  FooterMobileDisclaimer,
  FooterDesktopDisclaimer,
  FooterImageWrapper,
  SecondaryDisclaimerWrapper,
  FooterTopDisclaimer,
} from './footer.styled';
import { useHashAccountInfo } from 'hooks/use-hash-account-info';
import { validateEmail } from 'lib/utils/validate-email';
import LoginLocale from 'locale/en/login.json';
import MiscLocale from 'locale/en/misc.json';
import FooterLocale from 'locale/en/footer.json';
import { hashUserInfo } from 'lib/helpers/hash-user-info';
import {
  buildExtendedFooterHtml,
  footnoteMarkersToSupHtml,
  type FooterDisclaimerConfig,
} from './footer-disclaimer';
import SeedLogo from 'components/global/Logo';
import { CountrySelector } from './CountrySelector';
import { useFeatureFlags } from 'context-providers/feature-flag-provider';

interface FooterNavigationProps {
  backgroundImage: string;
  /** When set (e.g. PDP or journey extended footer), the secondary disclaimer HTML is derived from these lines. */
  footerDisclaimerConfig?: FooterDisclaimerConfig;
}

const FooterNavigation: FC<FooterNavigationProps> = ({
  backgroundImage,
  footerDisclaimerConfig = undefined,
}) => {
  const isSmall = useClientSideMediaQuery(960);
  const { featureFlags } = useFeatureFlags();
  const newsletterPlaceholder = isSmall
    ? 'Email'
    : 'Sign up for our Newsletter';

  const { hashedAccountInfo, isAccountInfoEmpty } = useHashAccountInfo();
  const [hasAlert, setHasAlert] = useState({
    msg: '',
    type: '',
  });
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const hideAlert = () => {
    setTimeout(() => {
      setIsLoading(false);
      setHasAlert({ msg: '', type: '' });
    }, 4000);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isLoading) return;
    setIsLoading(true);

    const email = e.currentTarget.newsletter.value;

    if (!validateEmail(email)) {
      setHasAlert({
        msg: MiscLocale.error_email_invalid,
        type: 'error',
      });
      hideAlert();
      return LoginLocale.error_email;
    }

    const { hashedEmail } = await hashUserInfo({ email });

    identifyAndTrackEvent(null, { email }, 'Email Submitted', {
      email,
      hashedEmail,
      hashedAccountInfo: isAccountInfoEmpty ? null : hashedAccountInfo,
      path: window.location.pathname,
    });

    setHasAlert({
      msg: MiscLocale.success_email_subscribed,
      type: 'success',
    });
    hideAlert();
  };

  const year = () => {
    const currentDate = new Date();
    return currentDate.getFullYear();
  };

  const { ref: footerInViewRef, inView: showImage } = useInView({
    triggerOnce: true,
    rootMargin: '0px 0px -100px 0px', // bottom of footer is +100px INSIDE viewport
  });

  const extendedFooter = footerDisclaimerConfig
    ? buildExtendedFooterHtml(footerDisclaimerConfig)
    : null;
  const disclaimer =
    extendedFooter?.disclaimer ??
    footnoteMarkersToSupHtml(FooterLocale.these_statements);
  const secondaryDisclaimer = extendedFooter?.secondaryDisclaimer;
  const disclaimerOnTop = footerDisclaimerConfig?.placement === 'top';

  return (
    <FooterWrapper>
      <FooterInner id='footer-content'>
        <FooterContent>
          {disclaimerOnTop && (
            <div>
              {secondaryDisclaimer && (
                <SecondaryDisclaimerWrapper
                  dangerouslySetInnerHTML={{ __html: secondaryDisclaimer }}
                />
              )}
              <FooterTopDisclaimer
                dangerouslySetInnerHTML={{ __html: disclaimer }}
              />
            </div>
          )}
          <SeedLogo light size='natural' fontSize='2.25rem' />
          <FooterHeading>
            <FooterSubhead as='p' color='inverse-primary'>
              Pioneering <MicroScope /> microbiome science [R+D] <UnionIcon />{' '}
              for human and planetary health <PlanetCapsule />
              since 2016.
            </FooterSubhead>
            <FooterForm onSubmit={handleSubmit}>
              <label htmlFor='newsletter'>
                Science with Seed—nerdy reads for your inbox.
              </label>
              <FooterCaption>
                By signing up you consent to receive Seed emails.
              </FooterCaption>
              <FooterNewsletter>
                <Input
                  type='email'
                  id='newsletter'
                  placeholder={newsletterPlaceholder}
                  disabled={isLoading}
                />
                <Submit type='submit' role='button'>
                  <ArrowRight color='#FCFCF7' />
                </Submit>
              </FooterNewsletter>
              {hasAlert.msg !== '' ? (
                <FormAlert
                  $backgroundColor={
                    hasAlert.type === 'error' ? 'var(--text-error)' : '#334e2b'
                  }
                >
                  {hasAlert.msg}
                </FormAlert>
              ) : null}
            </FooterForm>
          </FooterHeading>
          {!isSmall && !disclaimerOnTop ? (
            <>
              {secondaryDisclaimer && (
                <SecondaryDisclaimerWrapper
                  dangerouslySetInnerHTML={{ __html: secondaryDisclaimer }}
                />
              )}
              <FooterDesktopDisclaimer
                dangerouslySetInnerHTML={{ __html: disclaimer }}
              />
            </>
          ) : null}
        </FooterContent>
        <FooterNavColumn>
          <FooterNav footerNavigation={footerNavContent} />
          {featureFlags.internationalPricing ? (
            <FooterCountrySelectorArea>
              <CountrySelector />
            </FooterCountrySelectorArea>
          ) : null}
        </FooterNavColumn>
        {isSmall && !disclaimerOnTop ? (
          <FooterContent>
            {secondaryDisclaimer && (
              <SecondaryDisclaimerWrapper
                dangerouslySetInnerHTML={{ __html: secondaryDisclaimer }}
              />
            )}
            <FooterMobileDisclaimer
              dangerouslySetInnerHTML={{ __html: disclaimer }}
            />
          </FooterContent>
        ) : null}
      </FooterInner>
      <FooterFixedBottom
        id='footer-fixed-bg'
        style={{ backgroundImage: `url(${backgroundImage})` }}
      >
        <FixedHeadlineWrapper>
          <FooterImageWrapper>
            {showImage && (
              <NextImage
                src={
                  isSmall
                    ? 'https://res.cloudinary.com/dljz0lko8/image/upload/v1759319354/library/footer/awaken_optimized.gif'
                    : 'https://res.cloudinary.com/dljz0lko8/image/upload/v1759319354/library/footer/awaken_large_optimized.gif'
                }
                alt='Awaken Within'
                fill
                style={{ objectFit: 'contain' }}
              />
            )}
          </FooterImageWrapper>
        </FixedHeadlineWrapper>
        <Copyright as='p'>© {year()} Seed (Seed Health, Inc.)</Copyright>
      </FooterFixedBottom>
      <div ref={footerInViewRef} />
    </FooterWrapper>
  );
};

export default FooterNavigation;
